import React, { useState } from "react";

// The drawn parts of an Audit: the plan as it narrows, the steps as a line,
// the tool as a flow, the stack in layers. Each takes its data; none knows
// whose Audit it is.

const two = (n) => String(n).padStart(2, "0");
const money = (n) => `$${Math.round(n).toLocaleString("en-US")}`;

export const WAS = {
  ran: "Ran",
  slow: "Slow or patchy",
  stopped: "Did not happen",
};
export const WHO = {
  tool: "The tool",
  approve: "You approve",
  person: "A person",
  open: "Still open",
};

// The plan the volume comes from, drawn as it narrows.
export function Plan({ rows, note }) {
  return (
    <div data-testid="audit-plan">
      <ol className="space-y-1.5">
        {rows.map(([n, label], i) => (
          <li key={label} className="flex items-center gap-4">
            <span
              className="flex h-11 items-center rounded-[10px] bg-black px-4 text-[15px] text-white print:h-9"
              style={{
                width: `${Math.max(32, 100 - i * 17)}%`,
                opacity: Math.max(0.3, 1 - i * 0.14),
              }}
            >
              <span className="nm-display text-[20px] leading-none">{n}</span>
              <span className="ml-3 truncate text-[13px] text-white/70">
                {label}
              </span>
            </span>
          </li>
        ))}
      </ol>
      {note && (
        <p className="mt-3 text-[13px] leading-[1.4] text-black/50">{note}</p>
      )}
    </div>
  );
}

// The reader's own team. Nothing typed here leaves the page. Only the sample
// has this page: a client's Audit is already about their own team.
function Dial({ label, value, set, min, max, step = 1, show }) {
  return (
    <label className="block">
      <span className="flex items-baseline justify-between gap-4">
        <span className="text-[14px] text-black/60">{label}</span>
        <span className="nm-display text-[24px] leading-none tracking-[-0.02em]">
          {show(value)}
        </span>
      </span>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => set(Number(e.target.value))}
        className="mt-3 w-full accent-black"
      />
    </label>
  );
}
export function Yours({ mine, price }) {
  const toolPrice = Number(String(price).replace(/[^0-9]/g, ""));
  // From a check result: start from the visitor's own job.
  const startPeople = mine ? Math.min(mine.people, 12) : 4;
  const startHours = mine
    ? Math.max(
        1,
        Math.min(
          20,
          Math.round((mine.hours[0] + mine.hours[1]) / 2 / 4.33 / startPeople),
        ),
      )
    : 5;
  const [people, setPeople] = useState(startPeople);
  const [hours, setHours] = useState(startHours);
  const [rate, setRate] = useState(100);
  const month = Math.round((people * hours * 52) / 12);
  const year = people * hours * 52 * rate;
  const weeks = toolPrice / (people * hours * rate);
  const payback =
    weeks < 1
      ? "under a week"
      : weeks < 9
        ? `${Math.round(weeks)} week${Math.round(weeks) === 1 ? "" : "s"}`
        : `${Math.round(weeks / 4.33)} months`;
  return (
    <div data-testid="audit-yours" className="print:hidden">
      <p className="max-w-[640px] text-[18px] leading-[1.4] text-black/70 sm:text-[20px]">
        A larger team does not have this leak in one place. It has it in slices:
        an hour here from a partner, two there from a manager, every week. The
        job gets done, by the most expensive people in the building.
      </p>
      <div className="mt-8 grid gap-8 rounded-[14px] bg-[#f7f7f7] p-6 sm:grid-cols-3 sm:p-8">
        <Dial
          label="People who touch the job"
          value={people}
          set={setPeople}
          min={1}
          max={12}
          show={(v) => v}
        />
        <Dial
          label="Hours a week, each"
          value={hours}
          set={setHours}
          min={1}
          max={20}
          show={(v) => v}
        />
        <Dial
          label="What an hour of theirs costs"
          value={rate}
          set={setRate}
          min={30}
          max={300}
          step={10}
          show={(v) => `$${v}`}
        />
      </div>
      <dl className="mt-3 grid gap-px overflow-hidden rounded-[14px] bg-black/10 sm:grid-cols-3">
        {[
          [`${month}`, "hours a month"],
          [money(year), "a year, in their time"],
          [payback, `for a ${price} tool to pay for itself`],
        ].map(([value, label]) => (
          <div key={label} className="bg-black p-6 text-white">
            <dd
              className="nm-display text-[36px] leading-none tracking-[-0.03em] sm:text-[42px]"
              data-testid="yours-figure"
            >
              {value}
            </dd>
            <dt className="mt-3 text-[14px] leading-[1.35] text-white/65">
              {label}
            </dt>
          </div>
        ))}
      </dl>
      <p className="mt-4 text-[13px] leading-[1.45] text-black/50">
        {mine
          ? "Started from your check result. "
          : "Example values to start from. "}
        Move them to your own. The payback assumes the tool takes the whole job;
        in ours it took the research and the sending and left the judgment, so
        read it as the best case.
      </p>
    </div>
  );
}

// The steps as a line. Before: which ran, which were slow, which never
// happened. With the tool: who does each one now.
const WAS_STYLE = {
  ran: "bg-black text-white",
  slow: "bg-black/15 text-black",
  stopped: "border border-dashed border-black/40 text-black/40",
};
const NOW_STYLE = {
  tool: "bg-black text-white",
  approve: "bg-white text-black ring-2 ring-black",
  person: "bg-black/15 text-black",
  open: "border border-dashed border-black/40 text-black/40",
};
export function StepsStrip({ steps, view }) {
  const style = view === "before" ? WAS_STYLE : NOW_STYLE;
  const key = view === "before" ? "was" : "who";
  const legend = view === "before" ? WAS : WHO;
  return (
    <div className="mt-6 print:hidden" data-testid="steps-strip">
      <ol className="flex items-center">
        {steps.map((s, i) => (
          <li key={s.name} className="flex flex-1 items-center last:flex-none">
            <span
              title={s.name}
              className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[10px] tabular-nums transition-colors duration-500 sm:h-8 sm:w-8 ${style[s[key]] || style.open || style.slow}`}
            >
              {i + 1}
            </span>
            {i < steps.length - 1 && (
              <span
                className={`h-px flex-1 transition-colors duration-500 ${view === "before" && steps[i + 1].was === "stopped" ? "border-t border-dashed border-black/30" : "bg-black/30"}`}
              />
            )}
          </li>
        ))}
      </ol>
      <ul className="mt-4 flex flex-wrap gap-x-5 gap-y-2 text-[12px] text-black/55">
        {Object.entries(legend).map(([k, label]) => (
          <li key={k} className="flex items-center gap-2">
            <span className={`h-3 w-3 rounded-full ${style[k]}`} />
            {label}
          </li>
        ))}
      </ul>
    </div>
  );
}

// A line of boxes with arrows between them. The gate is drawn differently:
// it is where a person's decision sits.
function Line({ nodes }) {
  return (
    <ol className="flex flex-col gap-2 lg:flex-row lg:items-stretch lg:gap-0 print:flex-row print:items-stretch print:gap-0">
      {nodes.map((n, i) => (
        <React.Fragment key={n.label}>
          <li
            className={`flex-1 rounded-[12px] p-4 ${n.gate ? "bg-black text-white" : "bg-[#f7f7f7]"}`}
          >
            <p
              className={`text-[10px] font-medium uppercase tracking-[0.08em] ${n.gate ? "text-white/55" : "text-black/40"}`}
            >
              {n.label}
            </p>
            <ul className="mt-2 space-y-1">
              {n.items.map((item) => (
                <li key={item} className="text-[14px] leading-[1.3]">
                  {item}
                </li>
              ))}
            </ul>
          </li>
          {i < nodes.length - 1 && (
            <li
              aria-hidden="true"
              className="flex items-center justify-center px-1 text-[16px] text-black/35"
            >
              <span className="lg:hidden print:hidden">↓</span>
              <span className="hidden lg:inline print:inline">→</span>
            </li>
          )}
        </React.Fragment>
      ))}
    </ol>
  );
}
export function Flow({ flows }) {
  return (
    <div data-testid="audit-flow">
      {flows.map((flow, i) => (
        <React.Fragment key={flow.title || i}>
          {flow.title && (
            <p
              className={`text-[12px] uppercase tracking-[0.08em] text-black/45 ${i ? "mt-8" : ""}`}
            >
              {flow.title}
            </p>
          )}
          <div className="mt-3">
            <Line nodes={flow.nodes} />
          </div>
        </React.Fragment>
      ))}
      {flows.some((f) => f.nodes.some((n) => n.gate)) && (
        <p className="mt-3 text-[13px] text-black/50">
          Black boxes are where a person decides.
        </p>
      )}
    </div>
  );
}

export function Rules({ rules }) {
  return (
    <div className="mt-10 break-inside-avoid rounded-[14px] border border-black/15 p-6">
      <p className="text-[12px] uppercase tracking-[0.08em] text-black/45">
        The rules it runs under, in full
      </p>
      <ol className="mt-4 grid gap-x-8 gap-y-2.5 sm:grid-cols-2">
        {rules.map((rule, i) => (
          <li key={rule} className="flex gap-3 text-[15px] leading-[1.4]">
            <span className="w-5 shrink-0 text-[12px] tabular-nums text-black/35">
              {two(i + 1)}
            </span>
            {rule}
          </li>
        ))}
      </ol>
    </div>
  );
}

export function Choices({ list }) {
  return (
    <ol className="border-t border-black/10" data-testid="audit-choices">
      {list.map((c, i) => (
        <li
          key={c.chose}
          className="grid gap-x-6 gap-y-2 border-b border-black/10 py-5 sm:grid-cols-12"
        >
          <span className="text-[12px] tabular-nums text-black/35 sm:col-span-1 sm:pt-1">
            {two(i + 1)}
          </span>
          <div className="sm:col-span-4">
            <p className="text-[18px] leading-[1.2] tracking-[-0.015em]">
              {c.chose}
            </p>
            <p className="mt-1 text-[14px] text-black/45">
              <span className="mr-1.5 text-[10px] uppercase tracking-[0.08em]">
                over
              </span>
              <span className="line-through decoration-black/30">{c.over}</span>
            </p>
          </div>
          <p className="text-[15px] leading-[1.5] text-black/65 sm:col-span-7">
            {c.why}
          </p>
        </li>
      ))}
    </ol>
  );
}

// The stack in layers, the person on top.
export function Stack({ layers }) {
  return (
    <div data-testid="audit-stack">
      <ol className="space-y-2">
        {layers.map((layer, i) => (
          <li
            key={layer.layer}
            className={`rounded-[14px] p-5 sm:p-6 ${i === 0 ? "bg-black text-white" : "bg-[#f7f7f7]"}`}
          >
            <p
              className={`text-[10px] font-medium uppercase tracking-[0.08em] ${i === 0 ? "text-white/55" : "text-black/40"}`}
            >
              {layer.layer}
            </p>
            <dl
              className={`mt-3 grid gap-x-8 gap-y-4 ${layer.parts.length > 2 ? "sm:grid-cols-2 xl:grid-cols-4" : layer.parts.length === 2 ? "sm:grid-cols-2" : ""}`}
            >
              {layer.parts.map(([name, role]) => (
                <div key={name}>
                  <dt className="nm-display text-[22px] leading-none tracking-[-0.02em]">
                    {name}
                  </dt>
                  <dd
                    className={`mt-2 text-[14px] leading-[1.4] ${i === 0 ? "text-white/70" : "text-black/60"}`}
                  >
                    {role}
                  </dd>
                </div>
              ))}
            </dl>
          </li>
        ))}
      </ol>
      <p className="mt-5 max-w-[640px] text-[16px] leading-[1.45] text-black/65">
        The models will be replaced; better ones arrive every few months. The
        rules, the log and the record are written so that a different one can
        pick them up the next morning. That layer is the part you own.
      </p>
    </div>
  );
}

export function Risks({ list }) {
  return (
    <ul className="border-t border-black/10" data-testid="audit-risks">
      {list.map(([risk, held]) => (
        <li
          key={risk}
          className="grid gap-x-6 gap-y-1 border-b border-black/10 py-5 sm:grid-cols-12"
        >
          <p className="text-[17px] leading-[1.25] tracking-[-0.01em] sm:col-span-5">
            {risk}
          </p>
          <p className="text-[15px] leading-[1.5] text-black/65 sm:col-span-7">
            <span className="mr-2 text-[10px] uppercase tracking-[0.08em] text-black/40">
              Held by
            </span>
            {held}
          </p>
        </li>
      ))}
    </ul>
  );
}
